/**
 * Typewriter headings.
 *
 * Opt-in from Liquid via the text block's `enable_typewriter` setting, which
 * renders `data-typewriter="true"` plus a `data-lines` JSON array on the text
 * block wrapper (see snippets/text.liquid).
 *
 * Two things this deliberately does NOT do:
 *
 *   - animate when the visitor prefers reduced motion. An endless type/delete
 *     loop is precisely what that setting exists to stop, so we render the
 *     first line as static text and leave it alone.
 *   - watch the whole document for mutations. Shopify fires
 *     `shopify:section:load` when a section re-renders, which covers the theme
 *     editor and any AJAX section swap, at a fraction of the cost.
 */

const INIT_FLAG = 'typewriterInitialized';
const SELECTOR = '[data-typewriter="true"], typewriter-text';

/** Timers keyed by element, so a re-render can cancel the loop it replaced. */
const timers = new WeakMap();

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * Reads the configured lines, falling back to whatever text is already in the
 * element so a misconfigured block degrades to its own content.
 * @param {Element} el - The typewriter root.
 * @param {Element} target - The heading being animated.
 * @returns {string[]} The lines to cycle through.
 */
function readLines(el, target) {
  const raw = el.getAttribute('data-lines');

  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      const lines = parsed.map((line) => String(line).trim()).filter(Boolean);

      if (lines.length > 0) return lines;
    } catch (error) {
      console.warn('TypewriterText: invalid data-lines JSON', error);
    }
  }

  const fallback = target.textContent.trim();
  return fallback ? [fallback] : [];
}

/**
 * Starts the animation on an element.
 * @param {Element} el - The element carrying the typewriter attributes.
 */
function initTypewriter(el) {
  if (!(el instanceof HTMLElement) || el.dataset[INIT_FLAG]) return;

  // The block wrapper holds the settings; the heading inside holds the text.
  const target = el.querySelector('h1, h2, h3, h4, h5, h6, p') ?? el;
  const lines = readLines(el, target);

  if (lines.length === 0) return;

  el.dataset[INIT_FLAG] = 'true';

  // A single line has nothing to cycle to, so animating it only costs a reflow.
  if (lines.length === 1 || prefersReducedMotion()) {
    target.textContent = lines[0];
    return;
  }

  const typeSpeed = Number(el.getAttribute('data-type-speed')) || 90;
  const deleteSpeed = Number(el.getAttribute('data-delete-speed')) || 45;
  const pauseEnd = Number(el.getAttribute('data-pause-end')) || 2200;
  const pauseStart = Number(el.getAttribute('data-pause-start')) || 400;

  const longest = lines.reduce((a, b) => (b.length > a.length ? b : a));

  // The ghost reserves the width and height of the longest line. Without it the
  // heading resizes on every keystroke and drags the subheading and CTA with it.
  target.textContent = '';

  const root = document.createElement('span');
  root.className = 'typewriter';

  // Screen readers and SEO get one stable heading — the first line — rather
  // than a stream of half-typed words. Everything visual below is aria-hidden.
  const label = document.createElement('span');
  label.className = 'visually-hidden';
  label.textContent = lines[0];

  // Sizes the box to the longest line; `visibility: hidden` keeps it off-screen
  // and out of the accessibility tree while still reserving the space.
  const ghost = document.createElement('span');
  ghost.className = 'typewriter__ghost';
  ghost.setAttribute('aria-hidden', 'true');
  ghost.textContent = longest;

  const live = document.createElement('span');
  live.className = 'typewriter__live';
  live.setAttribute('aria-hidden', 'true');

  const content = document.createElement('span');
  content.className = 'typewriter__content';

  const cursor = document.createElement('span');
  cursor.className = 'typewriter__cursor';
  cursor.textContent = '|';

  live.append(content, cursor);
  root.append(label, ghost, live);
  target.append(root);

  let lineIndex = 0;
  let charIndex = 0;
  let isDeleting = false;

  function tick() {
    const line = lines[lineIndex % lines.length];

    charIndex += isDeleting ? -1 : 1;
    content.textContent = line.slice(0, charIndex);

    let delay = isDeleting ? deleteSpeed : typeSpeed;

    if (!isDeleting && charIndex === line.length) {
      delay = pauseEnd;
      isDeleting = true;
    } else if (isDeleting && charIndex === 0) {
      isDeleting = false;
      lineIndex++;
      delay = pauseStart;
    }

    timers.set(el, setTimeout(tick, delay));
  }

  tick();
}

/**
 * @param {ParentNode} root - The subtree to scan for typewriter elements.
 */
function scan(root) {
  for (const el of root.querySelectorAll(SELECTOR)) initTypewriter(el);
}

function init() {
  scan(document);

  // Covers the theme editor and any section re-render.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) scan(event.target);
  });

  // A replaced section leaves its old timer running against a detached node.
  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;

    for (const el of event.target.querySelectorAll(SELECTOR)) {
      clearTimeout(timers.get(el));
      timers.delete(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

class TypewriterText extends HTMLElement {
  connectedCallback() {
    initTypewriter(this);
  }

  disconnectedCallback() {
    clearTimeout(timers.get(this));
    timers.delete(this);
  }
}

if (!customElements.get('typewriter-text')) {
  customElements.define('typewriter-text', TypewriterText);
}
